import './AssetEditForm.scss';

import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Input, Modal, Select } from 'antd'
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Card, Row, Col, Button, Form, Badge, Alert } from 'react-bootstrap'
import { BdaDataset } from '../../models/BdaDataset'
import { BdaModel, BdaModelDTO } from '../../models/BdaModel'
import { Datasource, ObjectStorageDatasource } from '../../models/Datasource'
import { useToasts } from '../../contexts/toasts'
import { getAgentGenerationPayload, setObjectType, normalizePath, removePrefixPathFromAssetLocation, groupConfig } from '../../utils'
import { QueryDetails, VizClient } from '../../api/VizClient'
import classNames from 'classnames'
import IconButton from '../common/IconButton'
import UserItem from '../common/UserItem'
import { TailSpin } from 'react-loader-spinner';
import { Agents, GenerationResponseText } from '../../api/Agents';
import DatasourceExplorer from '../generics/DatasourceExplorer';
import ColumnsEditor from '../generics/ColumnsEditor';
import DatasetPreviewModal from '../generics/DatasetPreviewModal';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { sectionLabelsSelector } from '../../slices/sectionsSlice';
import { cloneDeep, set } from 'lodash';
import ModelCardSettingsForm from './ModelCardSettingsForm';
import MDEditor from '@uiw/react-md-editor';
import rehypeSanitize from "rehype-sanitize";
import { RedisVariables } from '../../api/RedisVariables';

interface AssetEditFormProps {
  modeLabel: string;
  mode: 'model' | 'dataset';
  asset: Partial<BdaDataset> | Partial<BdaModel>;
  onCancel: () => void;
  onSubmit: (asset: BdaDataset | BdaModelDTO) => void;
}

export default function AssetEditForm({ modeLabel, mode, asset, onCancel, onSubmit }: AssetEditFormProps) {
  const [validated, setValidated] = useState(false);
  const [selectedDatasource, setSelectedDatasource] = useState<Datasource>();
  const [selectedTable, setSelectedTable] = useState<any>();
  const [columns, setColumns] = useState<any[]>();
  const [selectedPath, setSelectedPath] = useState<string>();
  const [type, setType] = useState("table");
  const [editingAsset, setEditingAsset] = useState<Partial<BdaModel | BdaDataset> | undefined>(asset);
  const [generatedDescriptionLoading, setGeneratedDescriptionLoading] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [datasetPreviewError, setDatasetPreviewError] = useState(false);
  const [datasetPreview, setDatasetPreview] = useState<any[]>();
  const [datasourceDetails, setDatasourceDetails] = useState<Record<string, any>>();

  const { showToast } = useToasts();

  const datasetLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "datasets"));
  const datasourceLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "datasources"));

  const retrieveDatasourceDetails = useCallback(() => {
    const [namespace, key] = window.__env.DATASOURCE_DETAILS_REDIS_VARIABLE?.split(":") || ['system', 'datasourceDetails'];

    RedisVariables.getRedisVariable(key, namespace)
      .then(
        ({ data }) => {
          const details = data ? JSON.parse(data.value) : {};
          const parsedDetails = groupConfig(details);
          setDatasourceDetails(parsedDetails);
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('redis-variable-error', message, `Datasource Details Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [showToast]);

  const generateEnabled = useMemo(() => {
    return selectedTable && selectedDatasource?.type !== 'messageBroker' && mode === 'dataset';
  }, [selectedTable, mode, selectedDatasource]);

  const invalidAsset = useMemo(() => {
    if (!selectedDatasource) return false;
    if (mode === 'dataset') return selectedDatasource.type !== 'remote' && ((type === 'table' && !selectedTable) || (type === 'other' && !selectedPath));
    else return !selectedPath;
  }, [selectedDatasource, selectedPath, type, mode, selectedTable]);

  const initAsset = useCallback(() => {
    if (asset.id) {
      setSelectedDatasource(asset?.datasource);

      if (mode === 'dataset') {
        if (setObjectType<BdaDataset>(asset) && asset.datasetFileType) {
          setType(asset.datasetFileType.type || "table");
          if (asset.datasetFileType.type === "endpoint") return;
          const path = normalizePath(`${asset.datasetFileType.tableName || asset.datasetFileType.path}`);
          setSelectedPath(path);

          if (asset.datasetFileType.type === "table") {
            const array = asset.datasetFileType.tableName?.split("/") || [];
            const tableName = array[array.length - 1] || "";
            setSelectedTable({ name: tableName });
            setColumns(asset.datasetFileType.columns);
          }
        }
      }
    }
  }, [mode, asset]);

  useEffect(() => {
    initAsset();
    retrieveDatasourceDetails();
    // eslint-disable-next-line
  }, []);

  const submitDataset = useCallback(() => {
    if (invalidAsset) return;

    const body: BdaDataset = {
      id: editingAsset?.id,
      name: (editingAsset as BdaDataset)?.name,
      description: (editingAsset as BdaDataset)?.description,
      datasetFileType: {
        ...(editingAsset as BdaDataset).datasetFileType,
        type,
        columns,
        // TODO improve handling of other datasource types (prefixPath)
        path: type === "generic" ? removePrefixPathFromAssetLocation(selectedPath!, (selectedDatasource as any)?.prefixPath || "") : undefined,
        tableName: selectedDatasource?.type === 'objectStorage' && setObjectType<ObjectStorageDatasource>(selectedDatasource) ? removePrefixPathFromAssetLocation(selectedPath!, selectedDatasource.prefixPath) : undefined,
        details: (editingAsset as BdaDataset).datasetFileType?.details ? JSON.parse((editingAsset as BdaDataset).datasetFileType?.details) : undefined,
      },
      datasource: selectedDatasource,
      accessLevel: (editingAsset as BdaDataset)?.accessLevel,
      tags: (editingAsset as BdaDataset)?.tags,
    };
    if (body.datasetFileType!.tableName?.endsWith("/")) body.datasetFileType!.tableName = body.datasetFileType!.tableName.slice(0, -1);
    if (selectedDatasource?.type === "messageBroker") {
      body.datasetFileType!.type = "message";
      body.datasetFileType!.topic = selectedTable.name;
      body.datasetFileType!.group = "";
      delete body.datasetFileType!.tableName;
    }

    onSubmit(body);
  }, [editingAsset, columns, selectedDatasource, selectedTable, selectedPath, type, onSubmit, invalidAsset]);

  const submitModel = useCallback(() => {
    if (!selectedDatasource || !selectedPath || !(editingAsset as BdaModelDTO)?.format) return;

    const body: BdaModelDTO = {
      ...editingAsset as BdaModelDTO,
      datasource: selectedDatasource!,
      location: removePrefixPathFromAssetLocation(selectedPath, (selectedDatasource as any).prefixPath || ""),
    };

    onSubmit(body);
  }, [editingAsset, selectedDatasource, selectedPath, onSubmit]);

  const handleSubmit = useCallback((evt: React.FormEvent<HTMLFormElement>) => {
    const form = evt.currentTarget;
    evt.preventDefault();
    evt.stopPropagation();
    setValidated(true);

    if (form.checkValidity() === false) return;

    if (mode === 'model') submitModel();
    else submitDataset();
  }, [mode, submitDataset, submitModel]);

  const getDatasetColumns = useCallback(() => {
    const body = new QueryDetails(selectedDatasource!);
    body.tableName = (editingAsset as BdaDataset)?.datasetFileType?.tableName || selectedTable.name;
    if (selectedTable.prefixPath) body.prefixPath = selectedTable.prefixPath + "/";
    if (body.tableName!.endsWith("/")) body.tableName = body.tableName!.slice(0, -1);
    if (body.tableName!.includes(body.prefixPath!)) body.tableName = body.tableName!.replace(body.prefixPath!, "");
    // TODO is this needed?
    // if (body.prefixPath !== "/" && body.tableName?.includes(body.prefixPath)) body.tableName = body.tableName.replace(body.prefixPath, "");
    // if (selectedTable && selectedTable.prefixPath) body.prefixPath = normalizePath(selectedTable.prefixPath + "/");

    VizClient.getColumns(body)
      .then(
        ({ data }) => setColumns(data || []),
        (err) => {
          setColumns([]);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('columns-error', message, `Columns Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [selectedDatasource, selectedTable, showToast, editingAsset]);

  useEffect(() => {
    if (selectedTable && selectedDatasource?.type !== 'messageBroker' && mode === 'dataset') getDatasetColumns();
    // eslint-disable-next-line
  }, [selectedTable, mode, selectedDatasource]);

  const reloadSchema = useCallback(() => {
    setColumns(undefined);
    getDatasetColumns();
  }, [getDatasetColumns]);

  const handleFieldChange = (field: keyof BdaModel | keyof BdaDataset | string[]) => (evt: any) => {
    const value = field === "tags" || field === "accessLevel" || field === "format" ? evt : evt.target.value;
    setEditingAsset(curr => {
      if (Array.isArray(field)) {
        const newAsset = cloneDeep(curr!);
        set(newAsset, field, value);
        return newAsset;
      }
      return { ...curr!, [field]: value };
    });
  }

  const handleMarkdownChange = useCallback((value?: string) => {
    setEditingAsset(previous => ({
      ...previous!,
      markDown: {
        value: value || "",
      }
    }));
  }, []);

  const handleDetailsChange = useCallback((value: string | undefined) => {
    setEditingAsset(curr => ({
      ...curr,
      datasetFileType: {
        ...(curr as BdaDataset).datasetFileType,
        details: value,
      }
    }));
  }, []);

  const handlePathChange = useCallback((parts: string[], containsTable?: boolean) => {
    // removes empty parts (if any...needed?)
    const path = normalizePath(parts.filter(e => e).join('/'));
    setSelectedPath(path);
    setEditingAsset(curr => ({ ...curr, datasetFileType: { ...(curr as BdaDataset).datasetFileType, path } }));
    setSelectedTable(undefined);

    if (containsTable) {
      setColumns(undefined);
      const name = parts.at(-1); // tableName
      const prefixPath = parts.slice(0, -1).join("/");
      setSelectedTable({ name, prefixPath });
    }
  }, []);

  const handleSettingsClose = useCallback(() => setShowSettingsModal(false), []);
  const openSettings = useCallback(() => setShowSettingsModal(true), []);

  const openDatasetPreview = useCallback(() => {
    setShowPreviewModal(true);
    const body = new QueryDetails(selectedDatasource!);
    // body.tableName = (editingAsset as BdaDataset)?.datasetFileType?.tableName || selectedTable.name;

    VizClient.getDatasetPreview(body)
      .then(
        ({ data }) => setDatasetPreview(data || []),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          setDatasetPreviewError(true);
          setDatasetPreview([]);
          showToast('dataset-error', message, `${datasetLabels.singular} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [selectedDatasource, showToast, datasetLabels]);

  const handlePreviewClose = useCallback(() => {
    setShowPreviewModal(false);
    setDatasetPreviewError(false);
    setDatasetPreview(undefined);
  }, []);

  const handleDatasourceChange = useCallback((datasource: Datasource) => {
    setSelectedDatasource(datasource);
    if (mode === "dataset") {
      const datasourceTypeDetails = datasourceDetails && datasourceDetails[datasource.type] ? datasourceDetails[datasource.type] : undefined;
      const details = datasourceTypeDetails ? datasourceTypeDetails[datasource.driverType] : undefined;
      setEditingAsset(curr => ({
        ...curr,
        datasetFileType: {
          ...(curr as BdaDataset).datasetFileType,
          details,
        }
      }))
    }
  }, [mode, datasourceDetails]);

  const handleGenerateDescription = useCallback(() => {
    setGeneratedDescriptionLoading(true);
    const body = new QueryDetails(selectedDatasource);
    body.tableName = selectedPath;

    // TODO GET with query param to retrieve preview in csv format
    VizClient.getDatasetPreview(body)
      .then(
        ({ data }) => {
          const generationPayload = getAgentGenerationPayload(data);
          Agents.generate(generationPayload)
            .then(
              ({ data }) => {
                const agentMessage = data.messages.find((message) => message.role === "agent");
                if (agentMessage) {
                  const messageText = agentMessage.parts[0].text;
                  const generationResponseText = JSON.parse(messageText) as GenerationResponseText;
                  setEditingAsset(current => ({
                    ...current,
                    description: generationResponseText.Description,
                    tags: generationResponseText.Tags,
                  }));
                }
                setGeneratedDescriptionLoading(false);
              },
              (err) => {
                const message = err.response?.data?.message || err.message;
                const status = err.response?.status;
                showToast('generator-error', message, `Asset Description Generation Error ${status ? `(${status})` : ''}`, 'danger');
                setGeneratedDescriptionLoading(false);
              }
            )
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('dataset-error', message, `${datasetLabels.singular} Error ${status ? `(${status})` : ''}`, 'danger');
          setGeneratedDescriptionLoading(false);
        }
      )
  }, [showToast, selectedDatasource, selectedPath, datasetLabels]);

  return (
    <Form noValidate validated={validated} onSubmit={handleSubmit}>
      <Card.Body>
        <Row className="mb-2">
          <Col>
            <Form.Group controlId="asset-name" className="mb-2">
              <Form.Label>Name</Form.Label>
              <Form.Control size="sm" placeholder={`${modeLabel} Name *`} value={editingAsset!.name || ''} onChange={handleFieldChange('name')} required />
              <Form.Control.Feedback type="invalid">{modeLabel} name is required.</Form.Control.Feedback>
            </Form.Group>

            <Form.Group controlId="asset-tags">
              <Form.Label>Tags</Form.Label>
              <Select mode="tags" className="w-100" placeholder={`${modeLabel} Tags`} value={editingAsset!.tags} onChange={handleFieldChange('tags')} />
            </Form.Group>
          </Col>

          <Col>
            <Form.Group controlId="asset-description">
              <Form.Label>Description</Form.Label>
              <div className="textarea-wrapper">
                <Input.TextArea
                  rows={4}
                  disabled={generatedDescriptionLoading}
                  value={editingAsset!.description || ''}
                  onChange={handleFieldChange('description')}
                  className="textarea-with-suffix"
                  placeholder={`${modeLabel} description`}
                />
                {window.__env.RAGGER_ENABLED && (
                  <IconButton
                    tooltip="Generate with AI"
                    className="textarea-suffix"
                    disabled={generatedDescriptionLoading || !generateEnabled}
                    onClick={handleGenerateDescription}
                  >
                    {generatedDescriptionLoading
                      ? <TailSpin width={20} height={20} color="#677ab1" radius="1" />
                      : <FontAwesomeIcon icon={solid('wand-magic-sparkles')} />}
                  </IconButton>
                )}
              </div>
            </Form.Group>
          </Col>
        </Row>

        {mode === "model" && (
          <Row>
            <Col>
              <ModelCardSettingsForm model={editingAsset as BdaModel} onModelChange={setEditingAsset as React.Dispatch<React.SetStateAction<BdaModel | undefined>>} />
            </Col>
            <Col>
              <div data-color-mode="light" className="mb-2">
                <h6 className="d-lg-none">{modeLabel} Markdown</h6>
                <MDEditor
                  value={(editingAsset as BdaModel)?.markDown?.value || ""}
                  onChange={handleMarkdownChange}
                  previewOptions={{
                    rehypePlugins: [[rehypeSanitize]],
                  }}
                  textareaProps={{ placeholder: `Enter ${modeLabel} markdown here.` }}
                />
              </div>
            </Col>
          </Row>
        )}

        <br />

        <DatasourceExplorer
          mode={mode}
          type={type}
          onTypeChange={setType}
          asset={editingAsset}
          validated={validated}
          table={selectedTable}
          onTableChange={setSelectedTable}
          datasource={selectedDatasource}
          onDatasourceChange={handleDatasourceChange}
          onPathChange={handlePathChange}
          onDetailsChange={handleDetailsChange}
        />

        {selectedPath && (
          <Alert variant="primary" className="p-2 py-1">
            <FontAwesomeIcon icon={solid('check-circle')} />
            <span className="ms-2">Selected path: <b>{selectedPath}</b></span>
          </Alert>
        )}

        {(selectedTable && selectedDatasource?.type !== 'messageBroker' && mode === 'dataset') && (
          <ColumnsEditor columns={columns} onColumnsChange={setColumns}>
            <Button variant="light" className="border" size="sm" onClick={openSettings}>
              <FontAwesomeIcon icon={solid('cog')} />
              <span className="ms-2">Settings</span>
            </Button>
            <Button variant="light" className="border ms-2" size="sm" onClick={openDatasetPreview}>
              <FontAwesomeIcon icon={solid('eye')} />
              <span className="ms-2">{datasetLabels.singular} Preview</span>
            </Button>
            <Button variant="light" className="border text-primary ms-2" size="sm" onClick={reloadSchema}>
              <FontAwesomeIcon icon={solid('refresh')} />
              <span className="ms-2">Reload Schema</span>
            </Button>
          </ColumnsEditor>
        )}

        {validated && invalidAsset && (
          <Form.Control.Feedback type="invalid" className="d-block">You need to select a {datasourceLabels.singular} and a table or path to register a {mode}.</Form.Control.Feedback>
        )}
      </Card.Body>

      <div className="d-flex border-top p-2 px-3 justify-content-between">
        <Button type="button" size="sm" variant="light" className="border" onClick={onCancel}>
          <FontAwesomeIcon icon={solid('chevron-left')} />
          <span className="ms-2">Cancel</span>
        </Button>
        <Button type="submit" size="sm" variant="success">
          <FontAwesomeIcon icon={solid('save')} />
          <span className="ms-2">Save</span>
        </Button>
      </div>

      <DatasetPreviewModal
        show={showPreviewModal}
        error={datasetPreviewError}
        onClose={handlePreviewClose}
        preview={datasetPreview}
        title={`${selectedTable?.name || datasetLabels.singular} Preview`}
      />

      <Modal
        open={showSettingsModal}
        onCancel={handleSettingsClose}
        onClose={handleSettingsClose}
        title="Dataset Settings"
        okButtonProps={{ hidden: true }}
        cancelText="Close"
      >
        <Form.Group controlId="asset-delimiter" className="my-3">
          <Form.Label>Delimiter</Form.Label>
          <Form.Control size="sm" placeholder={`${modeLabel} Delimiter`} value={(editingAsset as BdaDataset).datasetFileType?.delimiter || ''} onChange={handleFieldChange(['datasetFileType', 'delimiter'])} />
        </Form.Group>
      </Modal>
    </Form>
  );
}

type Props = {
  datasource: Datasource;
  selected?: boolean;
  onClick: React.MouseEventHandler<HTMLDivElement>;
}

export const DatasourceListItem = memo(({ datasource, selected, onClick }: Props) => {
  return (
    <Card className={classNames("datasource-list-item", { selected })} onClick={onClick}>
      <Row className="gx-0 align-items-center mb-2">
        <Col className="overflow-hidden pe-2">
          <h6 className="mb-0 text-truncate">{datasource.name}</h6>
        </Col>

        {selected && (
          <Col xs={1}>
            <FontAwesomeIcon icon={solid('check')} size="lg" className="text-success" />
          </Col>
        )}
      </Row>

      <div className="d-flex justify-content-between align-items-center">
        <UserItem user={datasource.owner!} organization={datasource.organization!} className="sm" />
        <Badge bg="primary" className="w-auto">{datasource.accessLevel}</Badge>
      </div>
    </Card>
  )
});